import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BookOpen, Search, Folder, FileText, Tag, ChevronRight, Star,
  Clock, Eye, ArrowLeft, Paperclip, Download, ExternalLink, Link as LinkIcon,
  Loader2, Package, Wrench, Shield, AlertTriangle, CheckCircle, Building, Hammer,
  Lock
} from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

// Category icons mapping
const categoryIcons = {
  Package: Package,
  Wrench: Wrench,
  Shield: Shield,
  Tool: Hammer,
  Hammer: Hammer,
  FileText: FileText,
  CheckCircle: CheckCircle,
  AlertTriangle: AlertTriangle,
  Building: Building,
  Folder: Folder
};

// Category colors
const categoryColors = {
  blue: 'bg-blue-100 text-blue-700 border-blue-200',
  green: 'bg-green-100 text-green-700 border-green-200',
  red: 'bg-red-100 text-red-700 border-red-200',
  amber: 'bg-amber-100 text-amber-700 border-amber-200',
  purple: 'bg-purple-100 text-purple-700 border-purple-200',
  cyan: 'bg-cyan-100 text-cyan-700 border-cyan-200',
  orange: 'bg-orange-100 text-orange-700 border-orange-200',
  slate: 'bg-slate-100 text-slate-700 border-slate-200',
};

const KnowledgeBasePage = () => {
  const { articleSlug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [featuredArticles, setFeaturedArticles] = useState([]);
  const [recentArticles, setRecentArticles] = useState([]);
  const [tags, setTags] = useState([]);
  const [selectedArticle, setSelectedArticle] = useState(null);
  
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  
  // Category view
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [categoryArticles, setCategoryArticles] = useState([]);

  // Check if user is logged in (internal access)
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  
  useEffect(() => {
    const adminUser = localStorage.getItem('admin_user') || localStorage.getItem('adminUser');
    const adminLoggedIn = localStorage.getItem('adminLoggedIn');
    setIsLoggedIn(adminLoggedIn === 'true' && adminUser);
  }, []);

  useEffect(() => {
    fetchInitialData();
  }, []);

  useEffect(() => {
    if (articleSlug) {
      fetchArticle(articleSlug);
    } else {
      setSelectedArticle(null);
    }
  }, [articleSlug]);

  const fetchInitialData = async () => {
    try {
      const [categoriesRes, featuredRes, recentRes, tagsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/kb/categories`),
        axios.get(`${BACKEND_URL}/api/kb/articles/featured?limit=4`),
        axios.get(`${BACKEND_URL}/api/kb/articles/recent?limit=6`),
        axios.get(`${BACKEND_URL}/api/kb/tags`)
      ]);
      
      setCategories(categoriesRes.data);
      setFeaturedArticles(featuredRes.data);
      setRecentArticles(recentRes.data);
      setTags(tagsRes.data.slice(0, 15));
    } catch (error) {
      console.error('Error fetching KB data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchArticle = async (slug) => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kb/articles/${slug}`);
      setSelectedArticle(res.data);
    } catch (error) {
      console.error('Error fetching article:', error);
      setSelectedArticle(null);
    }
  };

  const fetchCategoryArticles = async (category) => {
    setSelectedCategory(category);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kb/articles?category_id=${category.id}&limit=50`);
      setCategoryArticles(res.data.items);
    } catch (error) {
      console.error('Error fetching category articles:', error);
    }
  };

  const handleSearch = async (e) => {
    e?.preventDefault();
    if (!searchQuery.trim()) {
      setSearchResults(null);
      return;
    }
    
    setSearching(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kb/search?q=${encodeURIComponent(searchQuery)}&limit=20`);
      setSearchResults(res.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setSearching(false);
    }
  };

  const fetchSuggestions = useCallback(async (query) => {
    if (query.length < 2) {
      setSuggestions([]);
      return;
    }
    try {
      const res = await axios.get(`${BACKEND_URL}/api/kb/search/suggestions?q=${encodeURIComponent(query)}`);
      setSuggestions(res.data.suggestions || []);
    } catch (error) {
      console.error('Suggestions error:', error);
    }
  }, []);

  const handleSearchInputChange = (e) => {
    const value = e.target.value;
    setSearchQuery(value);
    fetchSuggestions(value);
  };

  const clearSearch = () => {
    setSearchQuery('');
    setSearchResults(null);
    setSuggestions([]);
    setSelectedCategory(null);
  };

  // Render markdown content simply (basic implementation)
  const renderContent = (content) => {
    if (!content) return null;
    
    // Basic markdown rendering
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      // Headers
      if (line.startsWith('### ')) {
        return <h3 key={idx} className="text-lg font-semibold mt-6 mb-2">{line.slice(4)}</h3>;
      }
      if (line.startsWith('## ')) {
        return <h2 key={idx} className="text-xl font-bold mt-8 mb-3">{line.slice(3)}</h2>;
      }
      if (line.startsWith('# ')) {
        return <h1 key={idx} className="text-2xl font-bold mt-8 mb-4">{line.slice(2)}</h1>;
      }
      // List items
      if (line.startsWith('- ') || line.startsWith('* ')) {
        return <li key={idx} className="ml-4">{line.slice(2)}</li>;
      }
      // Numbered list
      if (/^\d+\.\s/.test(line)) {
        return <li key={idx} className="ml-4 list-decimal">{line.replace(/^\d+\.\s/, '')}</li>;
      }
      // Code blocks
      if (line.startsWith('```')) {
        return null; // Skip code block markers
      }
      // Bold text
      if (line.includes('**')) {
        const parts = line.split('**');
        return (
          <p key={idx} className="my-2">
            {parts.map((part, i) => i % 2 === 1 ? <strong key={i}>{part}</strong> : part)}
          </p>
        );
      }
      // Empty lines
      if (!line.trim()) {
        return <br key={idx} />;
      }
      // Regular paragraph
      return <p key={idx} className="my-2">{line}</p>;
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-cyan-600" />
        </div>
        <Footer />
      </div>
    );
  }

  // Article Detail View
  if (selectedArticle) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Header />
        
        {/* Breadcrumb */}
        <div className="bg-white border-b">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center gap-2 text-sm">
              <Link to="/knowledge-base" className="text-cyan-600 hover:underline">Knowledge Base</Link>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-600">{selectedArticle.category_name}</span>
              <ChevronRight className="w-4 h-4 text-slate-400" />
              <span className="text-slate-900 font-medium truncate">{selectedArticle.title}</span>
            </div>
          </div>
        </div>
        
        <main className="container mx-auto px-4 py-8">
          <div className="flex gap-8">
            {/* Main Content */}
            <article className="flex-1 max-w-4xl">
              <Button 
                variant="ghost" 
                onClick={() => navigate('/knowledge-base')}
                className="mb-4"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Knowledge Base
              </Button>
              
              <div className="bg-white rounded-xl shadow-sm p-8">
                {/* Article Header */}
                <div className="mb-6">
                  <div className="flex items-center gap-2 mb-3 flex-wrap">
                    <Badge className={categoryColors[categories.find(c => c.id === selectedArticle.category_id)?.color || 'slate']}>
                      {selectedArticle.category_name}
                    </Badge>
                    {selectedArticle.procedure_type && (
                      <Badge variant="outline" className="text-purple-600">{selectedArticle.procedure_type}</Badge>
                    )}
                    {selectedArticle.is_featured && (
                      <Badge className="bg-amber-100 text-amber-700">
                        <Star className="w-3 h-3 mr-1" />Featured
                      </Badge>
                    )}
                    {selectedArticle.access_level === 'internal' && (
                      <Badge variant="outline" className="text-slate-600">
                        <Lock className="w-3 h-3 mr-1" />Internal
                      </Badge>
                    )}
                  </div>
                  
                  <h1 className="text-3xl font-bold text-slate-900 mb-3">{selectedArticle.title}</h1>
                  
                  {selectedArticle.summary && (
                    <p className="text-lg text-slate-600 mb-4">{selectedArticle.summary}</p>
                  )}
                  
                  <div className="flex items-center gap-4 text-sm text-slate-500">
                    <span className="flex items-center gap-1">
                      <Clock className="w-4 h-4" />
                      Updated {new Date(selectedArticle.updated_at).toLocaleDateString()}
                    </span>
                    <span className="flex items-center gap-1">
                      <Eye className="w-4 h-4" />
                      {selectedArticle.view_count || 0} views
                    </span>
                  </div>
                </div>
                
                {/* Tags */}
                {selectedArticle.tags?.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-6 pb-6 border-b">
                    {selectedArticle.tags.map((tag) => (
                      <Badge key={tag} variant="secondary" className="cursor-pointer" onClick={() => {
                        setSearchQuery(tag);
                        handleSearch();
                        navigate('/knowledge-base');
                      }}>
                        <Tag className="w-3 h-3 mr-1" />
                        {tag}
                      </Badge>
                    ))}
                  </div>
                )}
                
                {/* Product Codes */}
                {selectedArticle.product_codes?.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-4 mb-6">
                    <h4 className="font-medium text-blue-900 mb-2">Related Product Codes</h4>
                    <div className="flex flex-wrap gap-2">
                      {selectedArticle.product_codes.map((code) => (
                        <Badge key={code} className="bg-blue-100 text-blue-700">{code}</Badge>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Content */}
                <div className="prose max-w-none">
                  {renderContent(selectedArticle.content)}
                </div>
                
                {/* Attachments */}
                {selectedArticle.attachments?.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <Paperclip className="w-5 h-5" />
                      Attachments ({selectedArticle.attachments.length})
                    </h3>
                    <div className="grid gap-2">
                      {selectedArticle.attachments.map((att) => (
                        <a
                          key={att.id}
                          href={`${BACKEND_URL}${att.url}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                        >
                          <FileText className="w-5 h-5 text-slate-500" />
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{att.original_name}</p>
                            <p className="text-xs text-slate-500">{(att.file_size / 1024).toFixed(1)} KB</p>
                          </div>
                          <Download className="w-5 h-5 text-cyan-600" />
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Links */}
                {selectedArticle.links?.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
                      <LinkIcon className="w-5 h-5" />
                      Related Links
                    </h3>
                    <div className="grid gap-2">
                      {selectedArticle.links.map((link) => (
                        <a
                          key={link.id}
                          href={link.url}
                          target={link.link_type === 'external' ? '_blank' : '_self'}
                          rel="noopener noreferrer"
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                        >
                          {link.link_type === 'external' ? (
                            <ExternalLink className="w-5 h-5 text-slate-500" />
                          ) : (
                            <LinkIcon className="w-5 h-5 text-slate-500" />
                          )}
                          <div className="flex-1">
                            <p className="font-medium text-slate-900">{link.title}</p>
                            {link.description && (
                              <p className="text-sm text-slate-500">{link.description}</p>
                            )}
                          </div>
                          <Badge variant="outline">{link.link_type}</Badge>
                        </a>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Related Articles */}
                {selectedArticle.related_articles_data?.length > 0 && (
                  <div className="mt-8 pt-6 border-t">
                    <h3 className="font-semibold text-slate-900 mb-4">Related Articles</h3>
                    <div className="grid gap-2">
                      {selectedArticle.related_articles_data.map((related) => (
                        <Link
                          key={related.id}
                          to={`/knowledge-base/${related.slug}`}
                          className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg hover:bg-slate-100"
                        >
                          <FileText className="w-5 h-5 text-cyan-600" />
                          <div>
                            <p className="font-medium text-slate-900">{related.title}</p>
                            {related.summary && (
                              <p className="text-sm text-slate-500 line-clamp-1">{related.summary}</p>
                            )}
                          </div>
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </article>
          </div>
        </main>
        
        <Footer />
      </div>
    );
  }

  // Main Knowledge Base View
  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-slate-900 via-slate-800 to-cyan-900 text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/10 rounded-full mb-6">
              <BookOpen className="w-5 h-5" />
              <span className="text-sm font-medium">Employee Knowledge Base</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Technical Documentation Center
            </h1>
            <p className="text-lg text-slate-300 mb-8">
              Search our comprehensive library of technical information, procedures, and product documentation.
            </p>
            
            {/* Search Box */}
            <form onSubmit={handleSearch} className="relative max-w-2xl mx-auto">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                value={searchQuery}
                onChange={handleSearchInputChange}
                placeholder="Search for products, procedures, specifications..."
                className="pl-12 pr-32 py-6 text-lg bg-white text-slate-900 rounded-xl"
              />
              <Button 
                type="submit" 
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-cyan-600 hover:bg-cyan-700"
                disabled={searching}
              >
                {searching ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Search'}
              </Button>
              
              {/* Search Suggestions */}
              {suggestions.length > 0 && (
                <div className="absolute left-0 right-0 top-full mt-2 bg-white rounded-lg shadow-lg border z-50 max-h-64 overflow-y-auto">
                  {suggestions.map((suggestion, idx) => (
                    <button
                      key={idx}
                      type="button"
                      onClick={() => {
                        setSearchQuery(suggestion);
                        setSuggestions([]);
                        handleSearch();
                      }}
                      className="w-full text-left px-4 py-2 text-slate-700 hover:bg-slate-100 flex items-center gap-2"
                    >
                      <Search className="w-4 h-4 text-slate-400" />
                      {suggestion}
                    </button>
                  ))}
                </div>
              )}
            </form>
            
            {/* Popular Tags */}
            {tags.length > 0 && (
              <div className="mt-6 flex flex-wrap justify-center gap-2">
                <span className="text-sm text-slate-400">Popular:</span>
                {tags.slice(0, 8).map((t) => (
                  <button
                    key={t.tag}
                    onClick={() => {
                      setSearchQuery(t.tag);
                      handleSearch();
                    }}
                    className="px-3 py-1 bg-white/10 hover:bg-white/20 rounded-full text-sm"
                  >
                    {t.tag}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>
      
      {/* Internal Access Notice */}
      {!isLoggedIn && (
        <div className="bg-amber-50 border-b border-amber-200">
          <div className="container mx-auto px-4 py-3">
            <div className="flex items-center justify-center gap-2 text-amber-800">
              <Lock className="w-4 h-4" />
              <span className="text-sm">
                Some content is restricted to logged-in employees.
                <Link to="/admin/login" className="ml-2 font-medium underline">Sign in</Link>
              </span>
            </div>
          </div>
        </div>
      )}
      
      <main className="container mx-auto px-4 py-12">
        {/* Search Results */}
        {searchResults && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold text-slate-900">
                Search Results for "{searchResults.query}"
              </h2>
              <Button variant="outline" onClick={clearSearch}>Clear Search</Button>
            </div>
            
            {searchResults.items.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Search className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No results found</h3>
                  <p className="text-slate-600">Try different keywords or browse categories below</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <p className="text-slate-600">{searchResults.total} results found</p>
                {searchResults.items.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Category View */}
        {selectedCategory && !searchResults && (
          <div className="mb-12">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center gap-3">
                {(() => {
                  const IconComponent = categoryIcons[selectedCategory.icon] || Folder;
                  return <IconComponent className="w-8 h-8 text-cyan-600" />;
                })()}
                <div>
                  <h2 className="text-2xl font-bold text-slate-900">{selectedCategory.name}</h2>
                  <p className="text-slate-600">{selectedCategory.description}</p>
                </div>
              </div>
              <Button variant="outline" onClick={() => setSelectedCategory(null)}>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>
            </div>
            
            {categoryArticles.length === 0 ? (
              <Card>
                <CardContent className="py-12 text-center">
                  <Folder className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-semibold mb-2">No articles in this category</h3>
                  <p className="text-slate-600">Check back later or browse other categories</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {categoryArticles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}
          </div>
        )}
        
        {/* Default View: Categories and Featured */}
        {!searchResults && !selectedCategory && (
          <>
            {/* Featured Articles */}
            {featuredArticles.length > 0 && (
              <section className="mb-12">
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Star className="w-6 h-6 text-amber-500" />
                  Featured Articles
                </h2>
                <div className="grid md:grid-cols-2 gap-4">
                  {featuredArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} featured />
                  ))}
                </div>
              </section>
            )}
            
            {/* Categories */}
            <section className="mb-12">
              <h2 className="text-2xl font-bold text-slate-900 mb-6">Browse by Category</h2>
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {categories.map((category) => {
                  const IconComponent = categoryIcons[category.icon] || Folder;
                  const colorClass = categoryColors[category.color] || categoryColors.slate;
                  return (
                    <Card 
                      key={category.id} 
                      className="cursor-pointer hover:border-cyan-300 hover:shadow-md transition-all"
                      onClick={() => fetchCategoryArticles(category)}
                    >
                      <CardContent className="p-6">
                        <div className={`w-12 h-12 rounded-lg ${colorClass} flex items-center justify-center mb-4`}>
                          <IconComponent className="w-6 h-6" />
                        </div>
                        <h3 className="font-semibold text-slate-900 mb-1">{category.name}</h3>
                        <p className="text-sm text-slate-600 line-clamp-2 mb-3">{category.description}</p>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-slate-500">{category.article_count || 0} articles</span>
                          <ChevronRight className="w-5 h-5 text-slate-400" />
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </section>
            
            {/* Recent Articles */}
            {recentArticles.length > 0 && (
              <section>
                <h2 className="text-2xl font-bold text-slate-900 mb-6 flex items-center gap-2">
                  <Clock className="w-6 h-6 text-slate-400" />
                  Recently Updated
                </h2>
                <div className="space-y-3">
                  {recentArticles.map((article) => (
                    <ArticleCard key={article.id} article={article} />
                  ))}
                </div>
              </section>
            )}
          </>
        )}
      </main>
      
      <Footer />
    </div>
  );
};

// Article Card Component
const ArticleCard = ({ article, featured = false }) => {
  return (
    <Link to={`/knowledge-base/${article.slug}`}>
      <Card className={`hover:border-cyan-300 hover:shadow-md transition-all ${featured ? 'bg-gradient-to-br from-white to-cyan-50' : ''}`}>
        <CardContent className="p-4">
          <div className="flex items-start gap-4">
            <div className={`p-2 rounded-lg ${featured ? 'bg-amber-100' : 'bg-slate-100'} shrink-0`}>
              <FileText className={`w-5 h-5 ${featured ? 'text-amber-600' : 'text-slate-600'}`} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <Badge variant="outline">{article.category_name}</Badge>
                {article.procedure_type && (
                  <Badge variant="outline" className="text-purple-600">{article.procedure_type}</Badge>
                )}
                {article.access_level === 'internal' && (
                  <Lock className="w-3 h-3 text-slate-400" />
                )}
              </div>
              <h3 className="font-semibold text-slate-900 mb-1">{article.title}</h3>
              {article.summary && (
                <p className="text-sm text-slate-600 line-clamp-2">{article.summary}</p>
              )}
              {article.tags?.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {article.tags.slice(0, 3).map((tag) => (
                    <span key={tag} className="px-2 py-0.5 bg-slate-100 text-slate-600 text-xs rounded">
                      {tag}
                    </span>
                  ))}
                </div>
              )}
              <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
                <span className="flex items-center gap-1">
                  <Eye className="w-3 h-3" />
                  {article.view_count || 0}
                </span>
                <span className="flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {new Date(article.updated_at).toLocaleDateString()}
                </span>
              </div>
            </div>
            <ChevronRight className="w-5 h-5 text-slate-400 shrink-0" />
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export default KnowledgeBasePage;
